import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createMarketingApi } from "../api";
import {
  allowedAudienceActions,
  allowedDeliveryActions,
  allowedNotificationActions,
  allowedTemplateActions,
  audienceStatusTone,
  CAMPAIGN_GOVERNANCE_ACTIONS,
  CAMPAIGN_REMEDIATION_ACTIONS,
  deliveryStatusTone,
  formatLabel,
  notificationStatusTone,
  templateStatusTone,
  toIsoUtc,
} from "../labels";
import type {
  AudienceAction,
  AudienceDto,
  AudienceMembershipDto,
  DeliveryDto,
  NotificationAction,
  NotificationDeliveryAction,
  NotificationDto,
  NotificationTemplateAction,
  TemplateDto,
} from "../types";
import {
  AddMembershipDialog,
  CampaignScheduleFields,
  ConfirmActionDialog,
} from "../components/MarketingDialogs";

export function CampaignDetailPage() {
  const { campaignId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<NotificationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [govOpen, setGovOpen] = useState(false);
  const [remOpen, setRemOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [highReach, setHighReach] = useState(false);
  const [isSensitive, setIsSensitive] = useState(true);
  const [companyWide, setCompanyWide] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Marketing", to: "/app/marketing" },
    { label: "Campaigns", to: "/app/marketing/campaigns" },
    { label: row?.id ?? "Campaign" },
  ]);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getNotification(campaignId);
      setRow(result.notification);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading campaign…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Campaign unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const allowed = allowedNotificationActions(row.status);
  const governance = allowed.filter((action) =>
    CAMPAIGN_GOVERNANCE_ACTIONS.includes(action),
  );
  const remediation = allowed.filter((action) =>
    CAMPAIGN_REMEDIATION_ACTIONS.includes(action),
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Campaign ${row.id}`}
        description={`${formatLabel(row.notification_type)} · ${formatLabel(row.channel)}`}
        actions={
          <Flex gap={8} wrap>
            {governance.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setScheduledAt("");
                  setGovOpen(true);
                }}
              >
                Review / schedule
              </Button>
            ) : null}
            {remediation.length > 0 ? (
              <Button
                variant="danger"
                onClick={() => {
                  setActionError(null);
                  setRemOpen(true);
                }}
              >
                Status remediation
              </Button>
            ) : null}
            {row.audience_id ? (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(`/app/marketing/audiences/${row.audience_id}`)
                }
              >
                Open audience
              </Button>
            ) : null}
            {row.notification_template_id ? (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    `/app/marketing/templates/${row.notification_template_id}`,
                  )
                }
              >
                Open template
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/marketing/deliveries?notification_id=${row.id}`,
                )
              }
            >
              Deliveries
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <StatusChip tone={notificationStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
        }
      />

      <Alert tone="info" title="Draft / review surface">
        prepare-deliveries is not available in HQ Marketing. Governance actions
        update notification status only. High-reach / sensitive / company-wide
        require Manager or Dual Approval IDs per API rules.
      </Alert>

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Campaign record
          </Text>
          <Text role="body" as="p">
            Template: {row.notification_template_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Audience: {row.audience_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Recipient: {row.recipient_user_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Source:{" "}
            {row.source_type && row.source_id
              ? `${row.source_type} / ${row.source_id}`
              : "—"}
          </Text>
          <Text role="body" as="p">
            Scheduled at: {row.scheduled_at ?? "—"}
          </Text>
          <Text role="body" as="p">
            Sent at: {row.sent_at ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Domain outbox events are not listed on this HQ endpoint. Status and
            timestamps above are the contract-backed audit fields for this
            record.
          </Text>
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={govOpen}
        title="Campaign governance"
        notice="submit_approval, approve, schedule, cancel, and archive. Schedule needs scheduled_at. Approval ID when high_reach / sensitive / company_wide."
        actions={governance}
        requireReason={false}
        extraFields={
          <CampaignScheduleFields
            scheduledAt={scheduledAt}
            onChange={setScheduledAt}
            highReach={highReach}
            onHighReach={setHighReach}
            isSensitive={isSensitive}
            onSensitive={setIsSensitive}
            companyWide={companyWide}
            onCompanyWide={setCompanyWide}
          />
        }
        onClose={() => setGovOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const action = input.action as NotificationAction;
            const body: Record<string, unknown> = {
              high_reach: highReach,
              is_sensitive: isSensitive,
              company_wide: companyWide,
            };
            if (input.reason) body.reason = input.reason;
            if (input.approval_id) body.approval_id = input.approval_id;
            if (action === "schedule") {
              if (!scheduledAt.trim()) {
                setActionError("scheduled_at is required for schedule");
                setBusy(false);
                return;
              }
              body.scheduled_at = toIsoUtc(scheduledAt);
            }
            await api.applyNotificationAction(row.id, action, body);
            toast.push({
              title: `Campaign ${formatLabel(action)}`,
              tone: "success",
            });
            setGovOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmActionDialog
        open={remOpen}
        title="Status remediation only"
        notice="mark_sent, mark_delivered, and fail update API status. They do not call the delivery worker or send email/push. Prefer worker-driven delivery in operations."
        actions={remediation}
        confirmVariant="danger"
        onClose={() => setRemOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const body: Record<string, unknown> = {};
            if (input.reason) body.reason = input.reason;
            if (input.approval_id) body.approval_id = input.approval_id;
            await api.applyNotificationAction(
              row.id,
              input.action as NotificationAction,
              body,
            );
            toast.push({
              title: `Remediation ${formatLabel(input.action)}`,
              tone: "success",
            });
            setRemOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function TemplateDetailPage() {
  const { templateId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<TemplateDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [markSensitive, setMarkSensitive] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Marketing", to: "/app/marketing" },
    { label: "Templates", to: "/app/marketing/templates" },
    { label: row?.name ?? "Template" },
  ]);

  const load = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getTemplate(templateId);
      setRow(result.template);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, templateId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading template…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Template unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedTemplateActions(row.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={row.name}
        description={`${formatLabel(row.notification_type)} · ${formatLabel(row.channel)}`}
        actions={
          actions.length > 0 ? (
            <Button
              variant="secondary"
              onClick={() => {
                setActionError(null);
                setActionOpen(true);
              }}
            >
              Lifecycle actions
            </Button>
          ) : null
        }
      />
      <ActionBar
        left={
          <StatusChip tone={templateStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
        }
      />
      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}
      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Template
          </Text>
          <Text role="body" as="p">
            ID: {row.id}
          </Text>
          <Text role="body" as="p">
            Classification: {formatLabel(row.classification)}
          </Text>
          <Text role="body" as="p">
            Content reference: {row.content_reference}
          </Text>
          <Text role="body" as="p">
            Owner: {row.owner_user_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Approved by: {row.approved_by_user_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Effective: {row.effective_from}
            {row.effective_to ? ` → ${row.effective_to}` : ""}
          </Text>
        </Stack>
      </Card>
      <ConfirmActionDialog
        open={actionOpen}
        title="Template lifecycle"
        notice="Sensitive approve requires Manager Approval ID (is_sensitive flag on the action)."
        actions={actions}
        requireReason={false}
        extraFields={
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={markSensitive}
              onChange={(event) => setMarkSensitive(event.target.checked)}
            />
            <Text role="small" as="span">
              Mark approve as sensitive (Manager Approval)
            </Text>
          </label>
        }
        onClose={() => setActionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const body: Record<string, unknown> = {
              is_sensitive: markSensitive,
            };
            if (input.reason) body.reason = input.reason;
            if (input.approval_id) body.approval_id = input.approval_id;
            await api.applyTemplateAction(
              row.id,
              input.action as NotificationTemplateAction,
              body,
            );
            toast.push({
              title: `Template ${formatLabel(input.action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function AudienceDetailPage() {
  const { audienceId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<AudienceDto | null>(null);
  const [memberships, setMemberships] = useState<AudienceMembershipDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [excludeId, setExcludeId] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Marketing", to: "/app/marketing" },
    { label: "Audiences", to: "/app/marketing/audiences" },
    { label: row?.name ?? "Audience" },
  ]);

  const load = useCallback(async () => {
    if (!audienceId) return;
    setLoading(true);
    setError(null);
    try {
      const [audienceResult, membershipResult] = await Promise.all([
        api.getAudience(audienceId),
        api.listMemberships(audienceId, { limit: 50, sort: "-id" }),
      ]);
      setRow(audienceResult.audience);
      setMemberships(membershipResult.memberships);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
      setMemberships([]);
    } finally {
      setLoading(false);
    }
  }, [api, audienceId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading audience…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Audience unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedAudienceActions(row.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={row.name}
        description="Audience definition and membership. Activate is status only — not a send."
        actions={
          <Flex gap={8} wrap>
            {actions.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Lifecycle actions
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => {
                setActionError(null);
                setAddOpen(true);
              }}
            >
              Add member
            </Button>
          </Flex>
        }
      />
      <ActionBar
        left={
          <StatusChip tone={audienceStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
        }
      />
      <Alert tone="warning" title="Sensitive audience data">
        No bulk recipient upload. Membership is one User ID at a time with
        confirmation. Support and Trust & Safety workflows stay independent.
      </Alert>
      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}
      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Definition
          </Text>
          <Text role="body" as="p">
            ID: {row.id}
          </Text>
          <Text role="body" as="p">
            Classification: {formatLabel(row.classification)}
          </Text>
          <Text role="body" as="p">
            Purpose: {row.purpose ?? "—"}
          </Text>
          <Text role="body" as="p">
            Owner: {row.owner_user_id ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            criteria_definition (opaque JSON):{" "}
            {JSON.stringify(row.criteria_definition)}
          </Text>
        </Stack>
      </Card>
      <Card>
        <Stack gap={12}>
          <Text role="h2" as="h2">
            Memberships
          </Text>
          {memberships.length === 0 ? (
            <EmptyState
              title="No memberships"
              description="Add a single user membership when needed for review."
              reason="none"
            />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th>Status</Th>
                  <Th>Included</Th>
                  <Th>Excluded</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {memberships.map((membership) => (
                  <Tr key={membership.id}>
                    <Td>{membership.user_id}</Td>
                    <Td>{formatLabel(membership.status)}</Td>
                    <Td>{membership.included_at}</Td>
                    <Td>{membership.excluded_at ?? "—"}</Td>
                    <Td>
                      {membership.status === "active" ? (
                        <Button
                          variant="danger"
                          onClick={() => setExcludeId(membership.id)}
                        >
                          Exclude
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      <ConfirmActionDialog
        open={actionOpen}
        title="Audience lifecycle"
        notice="approve → activate (status) → mark_updated / retire. Activate does not deliver messages."
        actions={actions}
        onClose={() => setActionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const body: Record<string, unknown> = {};
            if (input.reason) body.reason = input.reason;
            await api.applyAudienceAction(
              row.id,
              input.action as AudienceAction,
              body,
            );
            toast.push({
              title: `Audience ${formatLabel(input.action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <AddMembershipDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.addMembership(row.id, input);
            toast.push({ title: "Membership added", tone: "success" });
            setAddOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConfirmActionDialog
        open={excludeId != null}
        title="Exclude membership"
        notice="Exclude removes the user from the active audience membership. Confirm User ID intent before proceeding."
        actions={["exclude"]}
        confirmVariant="danger"
        onClose={() => setExcludeId(null)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!excludeId) return;
          setBusy(true);
          setActionError(null);
          try {
            const body: Record<string, unknown> = {};
            if (input.reason) body.reason = input.reason;
            await api.excludeMembership(row.id, excludeId, body);
            toast.push({ title: "Membership excluded", tone: "success" });
            setExcludeId(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function DeliveryDetailPage() {
  const { deliveryId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createMarketingApi(client), [client]);
  const toast = useToast();

  const [row, setRow] = useState<DeliveryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Marketing", to: "/app/marketing" },
    { label: "Deliveries", to: "/app/marketing/deliveries" },
    { label: row?.id ?? "Delivery" },
  ]);

  const load = useCallback(async () => {
    if (!deliveryId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getDelivery(deliveryId);
      setRow(result.delivery);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, deliveryId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading delivery…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Delivery unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const actions = allowedDeliveryActions(row.status);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={`Delivery ${row.id}`}
        description={`${formatLabel(row.channel)} · recipient ${row.recipient_user_id}`}
        actions={
          <Flex gap={8} wrap>
            {actions.length > 0 ? (
              <Button
                variant="danger"
                onClick={() => {
                  setActionError(null);
                  setActionOpen(true);
                }}
              >
                Remediation
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/marketing/campaigns/${row.notification_id}`,
                )
              }
            >
              Open campaign
            </Button>
          </Flex>
        }
      />
      <ActionBar
        left={
          <StatusChip tone={deliveryStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
        }
      />
      <Alert tone="warning" title="Status remediation only">
        Delivery actions update API status (cancel, suppress, fail, retry
        schedule markers). They do not transmit email or push. Prefer the
        delivery worker for real send paths.
      </Alert>
      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}
      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Performance / audit fields
          </Text>
          <Text role="body" as="p">
            Notification: {row.notification_id}
          </Text>
          <Text role="body" as="p">
            Sent at: {row.sent_at ?? "—"}
          </Text>
          <Text role="body" as="p">
            Delivered at: {row.delivered_at ?? "—"}
          </Text>
          <Text role="body" as="p">
            Failed at: {row.failed_at ?? "—"}
          </Text>
          <Text role="body" as="p">
            Retry count: {row.retry_count}
          </Text>
          <Text role="body" as="p">
            Failure reason: {row.failure_reason ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Metrics are limited to these contract fields. No invented open/click
            analytics.
          </Text>
        </Stack>
      </Card>
      <ConfirmActionDialog
        open={actionOpen}
        title="Delivery remediation"
        notice="Prefer cancel / suppress / mark_failed for ops. mark_sent and mark_delivered are status-only."
        actions={actions}
        confirmVariant="danger"
        onClose={() => setActionOpen(false)}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const body: Record<string, unknown> = {};
            if (input.reason) {
              body.reason = input.reason;
              if (input.action === "mark_failed") {
                body.failure_reason = input.reason;
              }
            }
            await api.applyDeliveryAction(
              row.id,
              input.action as NotificationDeliveryAction,
              body,
            );
            toast.push({
              title: `Delivery ${formatLabel(input.action)}`,
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}
